import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Printer, Globe, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const LANGUAGES = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', dir: 'rtl' },
  { code: 'ur', name: 'Urdu', dir: 'rtl' },
  { code: 'hi', name: 'Hindi', dir: 'ltr' },
  { code: 'bn', name: 'Bengali', dir: 'ltr' },
  { code: 'fr', name: 'French', dir: 'ltr' },
  { code: 'es', name: 'Spanish', dir: 'ltr' },
  { code: 'tr', name: 'Turkish', dir: 'ltr' },
  { code: 'tl', name: 'Filipino', dir: 'ltr' },
  { code: 'ml', name: 'Malayalam', dir: 'ltr' },
];

export function ReceiptPreview({ orderId, open, onClose }) {
  // iter609 — Persist language + bilingual preference so the cashier
  // never sees the modal twice once they've made the choice. The
  // "always print" toggle below also makes future prints go straight
  // to paper without ever showing this dialog.
  // iter613 — The defaults now come from the branch-level config (so
  // every iPad at the same branch starts in the same language). We
  // still keep a localStorage cache so the modal renders instantly,
  // then refresh from the server on open.
  const [lang, setLang] = useState(() => localStorage.getItem('pos_receipt_lang') || 'en');
  const [bilingual, setBilingual] = useState(() => localStorage.getItem('pos_receipt_bilingual') === '1');
  const [secondLang, setSecondLang] = useState(() => localStorage.getItem('pos_receipt_lang2') || 'ar');
  const [alwaysPrint, setAlwaysPrint] = useState(() => localStorage.getItem('pos_receipt_always_print') === '1');
  const [branchId, setBranchId] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);

  // iter613 — On open, read the cashier's branch and hydrate the
  // language config from the branch (overriding any stale per-device
  // localStorage). Failure leaves the cached value so the print still
  // works offline.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem('cashier_user');
        const me = raw ? JSON.parse(raw) : null;
        const bid = me?.branch_id;
        if (!bid) return;
        setBranchId(bid);
        const { data } = await api.get('/branches');
        const branch = (data || []).find((b) => b.id === bid);
        if (!branch || cancelled) return;
        if (branch.receipt_language) {
          setLang(branch.receipt_language);
          localStorage.setItem('pos_receipt_lang', branch.receipt_language);
        }
        if (typeof branch.receipt_bilingual === 'boolean') {
          setBilingual(branch.receipt_bilingual);
          localStorage.setItem('pos_receipt_bilingual', branch.receipt_bilingual ? '1' : '0');
        }
        if (branch.receipt_second_language) {
          setSecondLang(branch.receipt_second_language);
          localStorage.setItem('pos_receipt_lang2', branch.receipt_second_language);
        }
      } catch { /* ignore — fall back to local cache */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const generateReceipt = async () => {
    setLoading(true);
    try {
      const res = await api.post('/cashier/receipt/generate', {
        order_id: orderId,
        language: lang,
        bilingual,
        second_language: secondLang,
      });
      setReceipt(res.data);
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate receipt');
      return null;
    } finally { setLoading(false); }
  };

  // iter596 — Print via hidden iframe instead of `window.open('','_blank')`.
  // The new-tab approach was getting blocked by mobile popup-blockers and
  // also "took the user to another page" — disrupting the POS flow. The
  // iframe stays inside the current page, fires the browser print dialog
  // synchronously, then cleans itself up.
  const printViaIframe = (r) => {
    if (!r) return;
    const dir = r.is_rtl ? 'rtl' : 'ltr';
    const html = `<!DOCTYPE html><html dir="${dir}"><head><meta charset="UTF-8">
<style>
  body { font-family: 'Courier New', monospace; max-width: 300px; margin: 0 auto; padding: 10px; font-size: 12px; direction: ${dir}; }
  .center { text-align: center; }
  .bold { font-weight: bold; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
  .row { display: flex; justify-content: space-between; }
  .total-row { font-size: 14px; font-weight: bold; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 2px 0; }
  /* iter602 — Thermal receipt sizing for AirPrint / iPad: tells the
     browser this is a roll-fed receipt so it doesn't pad to A4. Works
     on iOS Safari AirPrint, Android Chrome, and desktop Chrome alike. */
  @page { size: 80mm auto; margin: 2mm; }
  @media print { body { margin: 0; max-width: none; width: 76mm; } }
</style></head><body>
<div class="center bold" style="font-size:14px">${r.company_name}</div>
<div class="center">${r.branch_name}</div>
${r.vat_number ? `<div class="center" style="font-size:10px">VAT: ${r.vat_number}</div>` : ''}
<div class="line"></div>
<div class="center bold">${r.labels.receipt_title}</div>
<div class="row"><span>${r.labels.order}: #${r.order_number}</span><span>${r.labels.date}: ${new Date(r.date).toLocaleDateString()}</span></div>
<div class="line"></div>
<div className="overflow-x-auto">
  <table>
  <tr><td class="bold">${r.labels.item}</td><td class="bold" style="text-align:center">${r.labels.qty}</td><td class="bold" style="text-align:${dir === 'rtl' ? 'left' : 'right'}">${r.labels.price}</td></tr>
  ${r.items.map(item => `<tr><td>${item.name}</td><td style="text-align:center">${item.quantity}</td><td style="text-align:${dir === 'rtl' ? 'left' : 'right'}">${r.currency} ${item.subtotal.toFixed(2)}</td></tr>`).join('')}
  </table>
</div>
<div class="line"></div>
<div class="row"><span>${r.labels.subtotal}</span><span>${r.currency} ${r.subtotal.toFixed(2)}</span></div>
${r.discount > 0 ? `<div class="row"><span>${r.labels.discount}</span><span>-${r.currency} ${r.discount.toFixed(2)}</span></div>` : ''}
<div class="row"><span>${r.labels.tax}</span><span>${r.currency} ${r.tax.toFixed(2)}</span></div>
<div class="line"></div>
<div class="row total-row"><span>${r.labels.grand_total}</span><span>${r.currency} ${r.total.toFixed(2)}</span></div>
<div class="line"></div>
<div class="row"><span>${r.labels.payment_method}</span><span>${r.payment_method}</span></div>
<div class="line"></div>
<div class="center" style="margin-top:8px">${r.labels.thank_you}</div>
<div class="center" style="font-size:9px;margin-top:4px">${r.labels.powered_by}</div>
</body></html>`;

    // Remove any pre-existing print iframe.
    const existing = document.getElementById('iter596-pos-print-iframe');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    const iframe = document.createElement('iframe');
    iframe.id = 'iter596-pos-print-iframe';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const trigger = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        toast.error('Print failed. Check your browser print permissions.');
      }
      // Give the print dialog ~1s grace then drop the iframe.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1000);
    };
    // Some browsers fire onload before doc.write completes; double-rAF
    // gives the renderer a tick to lay out before printing.
    if (iframe.contentWindow.document.readyState === 'complete') {
      requestAnimationFrame(() => requestAnimationFrame(trigger));
    } else {
      iframe.onload = trigger;
    }
  };

  const handlePrint = () => tryNativeThenIframe(receipt);

  // iter596 — One-click: generate AND print in the same action. Lets
  // the cashier hit a single button after the order is confirmed.
  const handleGenerateAndPrint = async () => {
    const r = await generateReceipt();
    if (r) tryNativeThenIframe(r);
  };

  // iter845l — Try the native SmartoPrinter Capacitor plugin FIRST on
  // Android native app. If the branch has a configured USB / Bluetooth /
  // Sunmi printer, native USB write bypasses Android's Default Print
  // Service entirely — which is invisible to thermal receipt printers.
  // Falls back to the iframe path when native isn't available or fails.
  const tryNativeThenIframe = async (r) => {
    if (!r) return;
    try {
      const { isNativeThermalAvailable, printNativeThermal } = await import('@/lib/nativeThermalPrint');
      if (isNativeThermalAvailable()) {
        // Fetch the branch's receipt printer to know its VID/PID + paper width.
        let printer = null;
        try {
          const { data } = await api.get('/printers');
          const list = Array.isArray(data) ? data : (data?.items || []);
          const branchPrinters = list.filter((p) => !p.branch_id || p.branch_id === branchId);
          const receiptPrinters = branchPrinters.filter((p) => (p.role || 'receipt') === 'receipt');
          const pool = receiptPrinters.length ? receiptPrinters : branchPrinters;
          printer = pool.find((p) => p.is_default) || pool[0] || null;
        } catch { /* ignore — will fall through to iframe */ }
        if (printer && ['usb', 'bluetooth', 'sunmi'].includes(printer.connection_type)) {
          // Convert the ReceiptPreview receipt shape → nativeThermalPrint payload.
          const payload = {
            company: r.company_name,
            title: r.labels?.receipt_title,
            order_number: r.order_number,
            date: r.date ? new Date(r.date).toLocaleString() : undefined,
            currency: r.currency,
            // iter934 — pass the logo URL so the native/USB thermal path can
            // render it (converted to a data URI before the canvas read).
            logo_url: r.thermal_logo_url || r.branch_logo_url || r.company_logo || undefined,
            items: (r.items || []).map((it) => ({ qty: it.quantity, name: it.name, subtotal: it.subtotal })),
            subtotal: r.subtotal,
            tax: r.tax,
            total: r.total,
            footer: r.labels?.thank_you,
          };
          const res = await printNativeThermal(printer, payload);
          if (res?.ok) {
            toast.success(`Receipt printed → ${printer.name} (USB)`);
            return;
          }
          toast.error(`Native USB print failed: ${res?.error || 'unknown'}. Falling back to OS print dialog.`);
        }
      }
    } catch { /* fall through */ }
    printViaIframe(r);
  };

  // iter609 — Persist preferences to localStorage so the next print
  // remembers the cashier's choice. Side-effect only inside event
  // handlers (not render) so React-hooks lint stays clean.
  // iter613 — Also push to the branch-level config (best-effort) so
  // every iPad at the branch follows along. localStorage stays as the
  // hot cache for offline / before-the-API-returns.
  const pushToBranch = (patch) => {
    if (!branchId) return;
    api.put(`/branches/${branchId}/receipt-settings`, patch).catch(() => { /* non-blocking */ });
  };
  const persistAndSetLang = (v) => {
    localStorage.setItem('pos_receipt_lang', v); setLang(v);
    pushToBranch({ receipt_language: v });
  };
  const persistAndSetBilingual = (v) => {
    localStorage.setItem('pos_receipt_bilingual', v ? '1' : '0'); setBilingual(v);
    pushToBranch({ receipt_bilingual: v });
  };
  const persistAndSetSecondLang = (v) => {
    localStorage.setItem('pos_receipt_lang2', v); setSecondLang(v);
    pushToBranch({ receipt_second_language: v });
  };
  const persistAlwaysPrint = (v) => { localStorage.setItem('pos_receipt_always_print', v ? '1' : '0'); setAlwaysPrint(v); };

  // iter609 — "Always print" mode: skip the modal entirely. As soon as
  // it's asked to open with an orderId, fire generate+print and close.
  useEffect(() => {
    if (open && orderId && alwaysPrint && !loading) {
      (async () => {
        const r = await generateReceipt();
        if (r) {
          printViaIframe(r);
          onClose?.();
        }
      })();
    }
  }, [open, orderId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-outfit flex items-center gap-2"><Printer size={18} />Receipt Preview</DialogTitle>
        </DialogHeader>

        {/* Language Controls */}
        <div className="grid grid-cols-2 gap-3 mb-1">
          <div>
            <Label className="text-xs flex items-center gap-1"><Globe size={10} />Primary Language</Label>
            <Select value={lang} onValueChange={persistAndSetLang}>
              <SelectTrigger className="h-8 text-xs" data-testid="receipt-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LANGUAGES.map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={bilingual} onChange={e => persistAndSetBilingual(e.target.checked)} className="rounded" data-testid="bilingual-toggle" />
                Bilingual
              </label>
            </Label>
            {bilingual && (
              <Select value={secondLang} onValueChange={persistAndSetSecondLang}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="receipt-lang2-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.filter(l => l.code !== lang).map(l => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
        </div>
        {/* iter616 — Visual hint that these settings are the BRANCH
            default (not per-device), so cashiers know any change here
            propagates to every iPad at the same branch. */}
        {branchId && (
          <p className="text-[10px] text-info/80 mb-3 flex items-center gap-1" data-testid="branch-default-hint">
            <Globe size={10} />
            <span>This is your <strong>branch default</strong> — changes apply to every iPad at this branch.</span>
          </p>
        )}

        {/* iter609 — One-time decision: don't ask again, just print. */}
        <label className="flex items-center gap-2 cursor-pointer mb-3 text-[11px] bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 text-emerald-800" data-testid="always-print-row">
          <input
            type="checkbox"
            checked={alwaysPrint}
            onChange={e => persistAlwaysPrint(e.target.checked)}
            className="rounded"
            data-testid="always-print-toggle"
          />
          <span><strong>Don&apos;t ask again</strong> — always print using this language without showing this dialog.</span>
        </label>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button size="sm" variant="outline" className="rounded-full" onClick={generateReceipt} disabled={loading} data-testid="generate-receipt-btn">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Printer size={14} className="mr-1" />}
            Preview only
          </Button>
          <Button size="sm" className="rounded-full" onClick={handleGenerateAndPrint} disabled={loading} data-testid="generate-and-print-btn">
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Printer size={14} className="mr-1" />}
            Generate & Print
          </Button>
        </div>

        {/* Receipt Preview */}
        {receipt && (
          <div className={`border rounded-lg p-4 bg-white font-mono text-xs space-y-1 ${receipt.is_rtl ? 'text-right' : 'text-left'}`} dir={receipt.direction} data-testid="receipt-preview">
            <div className="text-center font-bold text-sm">{receipt.company_name}</div>
            <div className="text-center text-[10px]">{receipt.branch_name} {receipt.branch_location && `- ${receipt.branch_location}`}</div>
            {receipt.vat_number && <div className="text-center text-[9px] text-muted-foreground">VAT: {receipt.vat_number}</div>}
            {receipt.customer && (
              <div className="text-[10px] text-muted-foreground">{receipt.labels.customer_label}: {receipt.customer.name}{receipt.customer.phone ? ` (${receipt.customer.phone})` : ''}</div>
            )}
            <div className="border-t border-dashed my-2" />
            <div className="text-center font-bold">{receipt.labels.receipt_title}</div>
            <div className="flex justify-between"><span>{receipt.labels.order}: #{receipt.order_number}</span><span>{new Date(receipt.date).toLocaleDateString()}</span></div>
            <div className="border-t border-dashed my-2" />
            {receipt.items.map((item, i) => (
              <div key={i} className="flex justify-between">
                <span>{item.name} x{item.quantity}</span>
                <span>{receipt.currency} {item.subtotal.toFixed(2)}</span>
              </div>
            ))}
            <div className="border-t border-dashed my-2" />
            <div className="flex justify-between"><span>{receipt.labels.subtotal}</span><span>{receipt.currency} {receipt.subtotal.toFixed(2)}</span></div>
            {receipt.discount > 0 && <div className="flex justify-between text-error"><span>{receipt.labels.discount}</span><span>-{receipt.currency} {receipt.discount.toFixed(2)}</span></div>}
            <div className="flex justify-between"><span>{receipt.labels.tax}</span><span>{receipt.currency} {receipt.tax.toFixed(2)}</span></div>
            <div className="border-t border-dashed my-2" />
            <div className="flex justify-between font-bold text-sm"><span>{receipt.labels.grand_total}</span><span>{receipt.currency} {receipt.total.toFixed(2)}</span></div>
            <div className="border-t border-dashed my-2" />
            <div className="flex justify-between"><span>{receipt.labels.payment_method}</span><span>{receipt.payment_method}</span></div>
            <div className="border-t border-dashed my-2" />
            <div className="text-center text-[10px] mt-2">{receipt.labels.thank_you}</div>
            <div className="text-center text-[8px] text-muted-foreground">{receipt.labels.powered_by}</div>
          </div>
        )}

        {receipt && (
          <Button className="w-full rounded-full mt-2" onClick={handlePrint} data-testid="print-receipt-btn">
            <Printer size={14} className="mr-1" />Print Receipt
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
